import { Combobox } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChartBinding, ChartBindingValue } from '@/entities/dashboards';

/** Диаграмма дашборда, которую можно назначить ведущей: своё название и идентификатор. */
export type SourceChartOption = {
  id: string;
  title: string;
};

type Props = {
  /** Разрез диаграммы; не задан — правило разреза не ждёт, и поля начального значения нет */
  sliceBinding?: ChartBinding;
  /** Начальное значение разреза */
  sliceValue: ChartBindingValue;
  onChangeSliceValue: (value: ChartBindingValue) => void;
  /** Правило ждёт выбранного в ведущей диаграмме — её и выбирают ниже */
  isSourceChartShown: boolean;
  /** Диаграммы дашборда, кроме самой правящейся: связь на себя разбором не была бы */
  sourceCharts: SourceChartOption[];
  sourceChartId: string | null;
  onChangeSourceChart: (value: string | null) => void;
};

/**
 * Поля мест подстановки диаграммы: начальное значение её разреза и ведущая диаграмма, выбор в
 * которой её наполняет.
 *
 * Поля показаны только тогда, когда правило этих мест ждёт: без разреза и без места под выбранное
 * задавать нечего, а пустые поля выглядели бы как незаполненные свойства.
 */
export const ChartBindingFields: FC<Props> = ({
  sliceBinding,
  sliceValue,
  onChangeSliceValue,
  isSourceChartShown,
  sourceCharts,
  sourceChartId,
  onChangeSourceChart,
}) => {
  const { t } = useTranslation('qualityDashboard');

  const sliceOptions = useMemo(
    () => (sliceBinding?.options ?? []).map((option) => ({ label: String(option), value: String(option) })),
    [sliceBinding?.options],
  );

  const sourceOptions = useMemo(
    () => sourceCharts.map((chart) => ({ label: chart.title, value: chart.id })),
    [sourceCharts],
  );

  const isSliceShown = !!sliceBinding && sliceOptions.length > 0;
  if (!isSliceShown && !isSourceChartShown) return null;

  const selectSliceValue = (selected: unknown) => {
    if (selected === undefined || selected === null) {
      onChangeSliceValue(null);
      return;
    }
    // Значение возвращается тем же видом, каким его дал разрез: число, ушедшее строкой, бэкенд
    // сравнивал бы с числовой колонкой как текст.
    const raw = String(selected);
    const original = (sliceBinding?.options ?? []).find((option) => String(option) === raw);
    onChangeSliceValue(original ?? raw);
  };

  const selectSourceChart = (selected: unknown) => {
    onChangeSourceChart(selected === undefined || selected === null ? null : String(selected));
  };

  return (
    <div className="grid grid-cols-1 gap-4 @md/chart-properties:grid-cols-2">
      {isSliceShown && (
        <Combobox
          label={t('presets.editor.sliceValue', { name: sliceBinding.name })}
          value={sliceValue === null || sliceValue === undefined ? undefined : String(sliceValue)}
          onChange={selectSliceValue}
          options={sliceOptions}
          isSearchable={sliceOptions.length > 10}
          className="w-full"
        />
      )}
      {isSourceChartShown && (
        <Combobox
          label={t('presets.editor.sourceChart')}
          value={sourceChartId ?? undefined}
          onChange={selectSourceChart}
          options={sourceOptions}
          emptyMessage={t('presets.editor.sourceChartEmpty')}
          isSearchable={false}
          className="w-full"
        />
      )}
    </div>
  );
};
